import { Link } from "react-router-dom";

export type GossipArticle = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  categoryId: string;
  cover: string;
  author: string;
  date: string;
  updated: string;
  views: number;
  comments: number;
  heat: number;
  featured: boolean;
  breaking: boolean;
  tags: string[];
  content: string[];
};

const formatCount = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)} 万` : `${value}`;

type ArticleCardProps = {
  article: GossipArticle;
  variant?: "vertical" | "horizontal" | "minimal";
  index?: number;
};

export default function ArticleCard({ article, variant = "vertical", index }: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4 border-b border-background-200 py-5 last:border-none">
        {typeof index === "number" ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-sm font-bold text-primary-600">
            {index + 1}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2.5 text-xs text-foreground-500">
            <Link
              to={`/hot?cat=${article.categoryId}`}
              className="rounded bg-primary-100 px-2 py-0.5 font-medium text-primary-800 transition-colors hover:bg-primary-200"
            >
              {article.category}
            </Link>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-sm" aria-hidden="true" />
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line text-sm" aria-hidden="true" />
              {article.comments}
            </span>
            <span>{article.date}</span>
          </div>
          <h3 className="font-heading text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            <Link to={`/post/${article.id}`}>{article.title}</Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.excerpt}</p>
        </div>
        <Link to={`/post/${article.id}`} className="hidden shrink-0 sm:block" aria-hidden="true" tabIndex={-1}>
          <div className="h-[86px] w-[132px] overflow-hidden rounded-md">
            <img
              src={article.cover}
              alt={article.title}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
      </article>
    );
  }

  if (variant === "minimal") {
    return (
      <article className="group flex items-start gap-3 border-b border-background-200 py-4 last:border-none">
        <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent-100 font-heading text-xs font-bold text-accent-700">
          {typeof index === "number" ? index + 1 : "·"}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-700">
            <Link to={`/post/${article.id}`}>{article.title}</Link>
          </h3>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-primary-700">{article.category}</span>
            <span className="text-background-400">|</span>
            <span>{formatCount(article.views)} 阅读</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/post/${article.id}`} className="block overflow-hidden">
        <div className="h-[196px] w-full overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <Link
            to={`/hot?cat=${article.categoryId}`}
            className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {article.category}
          </Link>
          {article.breaking ? (
            <span className="flex items-center gap-1 rounded bg-accent-500 px-2 py-0.5 font-medium text-background-50">
              <i className="ri-flashlight-fill text-xs" aria-hidden="true" />
              热
            </span>
          ) : null}
        </div>
        <h3 className="font-heading text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          <Link to={`/post/${article.id}`}>{article.title}</Link>
        </h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-6 text-foreground-600">{article.excerpt}</p>
        <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line text-sm" aria-hidden="true" />
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-sm" aria-hidden="true" />
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line text-sm" aria-hidden="true" />
              {article.comments}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}